/**
 * Email sign-up path only. The account is created by the code verify, which
 * carries no identity fields, so the date of birth and username held from the
 * registration form are posted right after, with the token that call returned.
 *
 * Invalidates the profile so the root identity modal does not re-ask for what
 * was just given. A failure here does not fail the login; the modal is the
 * backstop.
 */
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {Token, UsersApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {USE_GET_USER_PROFILE_KEY} from "@/hooks/useGetUserProfile";

interface SignupIdentityInput {
    token: Token;
    dateOfBirth?: number;
    nickname?: string;
}

export function useCreateSignupIdentity() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({token, dateOfBirth, nickname}: SignupIdentityInput): Promise<void> => {
            const api = new UsersApi(getConfig(token));
            if (dateOfBirth !== undefined) {
                await api.createUsersDateOfBirth({setDateOfBirthBody: {dateOfBirth}});
            }
            if (nickname) {
                await api.createUsersLeaderboardNickname({updateNicknameBody: {nickname}});
            }
            await queryClient.invalidateQueries({queryKey: [USE_GET_USER_PROFILE_KEY]});
        },
    });
}
